import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError, eur, openFilePreview } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function SickLeave() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/sick-leave").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, patch) => { try { await api.put(`/sick-leave/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  return (
    <AdminLayout nav={adminNav} title="Sick Leave">
      <PageHeader title="Sick Leave" subtitle="Record and certify absences" />
      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Employee</th><th className="p-3">First day</th><th className="p-3">Return</th><th className="p-3">Days</th><th className="p-3">Reason</th><th className="p-3">Cert</th><th className="p-3">Certified</th><th className="p-3">Pay type</th><th className="p-3 text-right">Sick Pay</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-zinc-400">No records.</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} data-testid={`sick-row-${r.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                <td className="p-3 font-medium">{r.employee_name}</td>
                <td className="p-3 num">{fmtDate(r.first_day)}</td>
                <td className="p-3 num">{fmtDate(r.actual_return)}</td>
                <td className="p-3 num">{r.sick_pay?.total_days ?? "—"}</td>
                <td className="p-3">{r.reason_category}</td>
                <td className="p-3">{r.cert_file ? <button data-testid={`admin-view-cert-${r.id}`} onClick={() => { if (!openFilePreview(r.cert_file)) toast.error("Unable to open this file"); }} className="text-xs text-amber-600 font-semibold underline">View</button> : <span data-testid={`cert-missing-${r.id}`} className="text-[10px] font-semibold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">Missing</span>}</td>
                <td className="p-3">
                  <Button data-testid={`cert-${r.id}`} size="sm" variant={r.certified ? "default" : "outline"} className={r.certified ? "bg-emerald-600 text-white h-8" : "h-8"} onClick={() => update(r.id, { certified: !r.certified })}>
                    {r.certified ? "Certified" : "Uncertified"}
                  </Button>
                </td>
                <td className="p-3">
                  <Select value={r.pay_type} onValueChange={(v) => update(r.id, { pay_type: v })}>
                    <SelectTrigger data-testid={`pay-${r.id}`} className="h-8 w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{["statutory", "company", "unpaid"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="p-3 num text-right font-semibold" data-testid={`sick-pay-${r.id}`} title={r.sick_pay ? `${r.sick_pay.paid_days} paid days @ €${r.sick_pay.daily_rate}/day` : ""}>{eur(r.sick_pay?.amount || 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AdminLayout>
  );
}
